<template>
    <PageContentLoader v-if="(fetching || stale) && !jewelleryItem" />

    <PageErrorFiveHundred
        v-else-if="error"
        :errors="responseErrors"
    >
        <ThButton
            :loading="fetching"
            @click="executeQuery"
        >
            {{ t('globals.try_again') }}
        </ThButton>
    </PageErrorFiveHundred>

    <div v-else-if="jewelleryItem">
        <ProductBreadcrumbs :breadcrumbs />

        <div
            :class="classProductWrapper"
            class="product-wrapper"
        >
            <ProductGalleryNew :product="jewelleryItem" />

            <div class="product-details">
                <div
                    v-qa="'product-information'"
                    :data-qa-product-properties="
                        JSON.stringify({
                            name: jewelleryItem.productName,
                            metal: jewelleryItem.primaryMetal.translation.name,
                            price: {
                                currency:
                                    jewelleryItem.salePrice.total.currency,
                                amount: jewelleryItem.salePrice.total.amount,
                            },
                        })
                    "
                    class="product-information"
                >
                    <div class="product-title">
                        <h1
                            v-qa="'jewellery-name'"
                            class="name"
                        >
                            {{ jewelleryItem.productName }}
                        </h1>

                        <SaveForLaterButton
                            :product="jewelleryItem"
                            page-type="pdp"
                        />
                    </div>

                    <ReviewsAggregate
                        v-if="jewelleryItem.reviewsAggregate.total"
                        :data="jewelleryItem.reviewsAggregate"
                    />

                    <p
                        v-if="jewelleryItem.translation.description"
                        v-qa="'jewellery-description'"
                        class="description"
                    >
                        {{ jewelleryItem.translation.description }}
                    </p>

                    <TaxSnippet :data="jewelleryItem.salePrice" />

                    <TooltipCustom page-type="product-details">
                        <!-- TODO translate -->
                        <template #label>Price explained</template>

                        <!-- TODO translate -->
                        <template #title>Price explained</template>

                        <!-- TODO translate -->
                        <template #content>
                            <ul class="price-explainer-list">
                                <li class="item">
                                    <!-- TODO translate -->
                                    <span class="label"> Item price </span>

                                    <span class="value">
                                        <Money
                                            :data="
                                                jewelleryItem.salePrice.total
                                            "
                                            :formatter
                                        />
                                    </span>
                                </li>

                                <li class="item">
                                    <!-- TODO translate -->
                                    <span class="label"> Shipping </span>

                                    <!-- TODO translate -->
                                    <span class="value"> Free & Insured </span>
                                </li>

                                <li class="item">
                                    <!-- TODO translate -->
                                    <span class="label"> Warranty </span>

                                    <!-- TODO translate -->
                                    <span class="value"> Lifetime </span>
                                </li>

                                <hr class="divider" />

                                <li class="item">
                                    <!-- TODO translate -->
                                    <span class="label"> Total </span>

                                    <span class="value">
                                        <TaxSnippet
                                            :data="jewelleryItem.salePrice"
                                            :inline="true"
                                        />
                                    </span>
                                </li>
                            </ul>
                        </template>
                    </TooltipCustom>

                    <FinanceOptions
                        :affirm="jewelleryItem.financeOptionsAffirm"
                        :klarna="jewelleryItem.financeOptionsKlarna"
                    />
                </div>

                <div class="product-customization">
                    <MetalVariations
                        :selected="jewelleryItem"
                        :variations="jewelleryItem.variations"
                        class="metal"
                    />

                    <div
                        v-if="
                            jewelleryItem.__typename ===
                                'CatalogueJewelleryRing' ||
                            !jewelleryItem.engravingDisabled
                        "
                        class="size-and-engraving"
                    >
                        <ThSelect
                            v-if="
                                jewelleryItem.__typename ===
                                'CatalogueJewelleryRing'
                            "
                            v-model="ringSizeId"
                            v-qa="'ring-size'"
                            :error="ringSizeError"
                            :legend="'Ring size'"
                            :options="ringSizeOptions"
                            :placeholder="'Select'"
                            class="size"
                            serif-font
                        >
                            <template #tooltip>
                                <LazyTooltip
                                    inline
                                    path="/jewellery/ring-size"
                                    page-type="product-details"
                                />
                            </template>
                        </ThSelect>

                        <TextField
                            v-if="!jewelleryItem.engravingDisabled"
                            :id="'engraving'"
                            v-model="engraving"
                            :label="'Optional engraving'"
                            :maxlength="engravingMaxLength"
                            class="engraving"
                            lazy
                            serif-font
                            type="text"
                        >
                            <template #tooltip>
                                <LazyTooltip
                                    inline
                                    path="/jewellery/engraving"
                                    page-type="product-details"
                                />
                            </template>
                        </TextField>
                    </div>
                </div>

                <DeliveryTimeframe
                    v-if="deliveryDate"
                    :data="deliveryDate"
                />

                <DeliveryTimeframeExpress
                    v-if="jewelleryItem.supportsExpress"
                />

                <AddToCartFloatingButton>
                    <template #button>
                        <div class="product-cta">
                            <ThButton
                                :full-width="true"
                                :loading="fetchingExistingCustomer"
                                type="solid"
                                @click="handleAddToCart"
                            >
                                Add to cart
                            </ThButton>
                        </div>
                    </template>
                </AddToCartFloatingButton>

                <PdpAccordion
                    :items="data?.pageProductDetails?.accordionItems ?? []"
                    about-this-entity-label="design"
                    :resizeable="
                        jewelleryItem.__typename === 'CatalogueJewelleryRing' &&
                        jewelleryItem.isResizeable
                    "
                    :product="jewelleryItem"
                    :related-products="{
                        products: jewelleryItem.relatedProducts,
                    }"
                >
                    <template
                        v-if="jewelleryItem?.translation?.seoText"
                        #aboutThisProduct
                    >
                        <div class="product-additional-details">
                            <div class="product-description">
                                <!-- eslint-disable vue/no-v-html -->
                                <p
                                    class="description"
                                    v-html="
                                        localizeHrefs(
                                            jewelleryItem.translation.seoText
                                        )
                                    "
                                ></p>
                            </div>
                        </div>
                    </template>
                </PdpAccordion>

                <AuthFormInModal
                    :is-open="isEnquiryModalOpen"
                    :registration-disabled="fetchingNewCustomer"
                    :registration-errors="errors"
                    :content
                    @close="closeEnquiryModal"
                    @register="submitNewCustomer"
                    @login="handleAddToCart"
                >
                </AuthFormInModal>
            </div>
        </div>

        <CmsBlocks
            :data="data?.pageProductDetails?.blocks"
            :product="jewelleryItem"
            :related-products="{
                products: jewelleryItem.relatedProducts,
            }"
            page-type="product-details"
        />

        <Reviews :data="jewelleryItem.reviews" />
    </div>

    <PageErrorFourHundredFour v-else />
</template>

<script lang="ts" setup>
import {
    computed,
    defineAsyncComponent,
    hydrateOnIdle,
    hydrateOnVisible,
    ref,
    toRef,
    watch,
} from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { storeToRefs } from 'pinia'
import { useI18n } from 'vue-i18n'
import { useRedirect } from 'wucdbm-vite-vue-ssr'

import {
    type DesignStudioJewelleryInput,
    type InputMaybe,
    type MetalCode,
    type RingSizeInput,
} from '@/graphql/api'
import {
    createResponseAndPath,
    useLastResponseErrorAtPath,
    useResponseError,
} from '@/helpers/errors'
import type { JewelleryProductType } from '@/router'
import { useRegionPlugin } from '@/plugins/region'
import { useTranslator } from '@/i18n'
import { getCommissionJunctionInput } from '@/helpers/commission-junction'
import { useHandleAddToCartSuccess } from '@/views/product/composables/useHandleAddToCartSuccess'
import { getUri } from '@/helpers/getCurrentUri'
import { useCustomerRegistrationInputStore } from '@/composables/useCustomerRegistrationInput'
import { useProductAnalytics } from '@/views/product/composables/analytics/useProductAnalytics'
import { useProductPageMeta } from '@/views/product/composables/useProductPageMeta'
import { useDeliveryDate } from '@/views/product/composables/useDeliveryDate'
import { useJewelleryDetailsBreadcrumbs } from './useJewelleryDetailsBreadcrumbs'
import { useRoutes } from '@/router/routes'
import { useHrefLocalizer } from '@/router/href-localizer'
import { useRingSize } from '@/domain/product/ring/useRingSize'
import { useIsAdmin } from '@/components/app/isAdmin'
import { useCurrencyInput } from '@/plugins/currency-input.ts'
import { useMoneyFormatter } from '@/plugins/money-formatter.ts'
import { useAuth } from '@/plugins/auth.ts'
import { useJewelleryDetailsQuery } from '@/views/product/jewellery/Jewellery.generated.ts'
import { useAddToCartJewelleryExistingCustomerMutation } from '@/graphql/api/cart/jewellery/mutations/addToCartJewelleryExistingCustomer.mutation.generated.ts'
import { useAddToCartJewelleryNewCustomerMutation } from '@/graphql/api/cart/jewellery/mutations/addToCartJewelleryNewCustomer.mutation.generated.ts'

import ThButton from '@/components/ui/buttons/ThButton.vue'
import TaxSnippet from '@/components/rings/TaxSnippet.vue'
import TextField from '@/components/ui/inputs/TextField.vue'
import DeliveryTimeframe from '@/components/rings/DeliveryTimeframe.vue'
import MetalVariations from '@/components/rings/MetalVariations.vue'
import FinanceOptions from '@/components/rings/FinanceOptions.vue'
import ProductBreadcrumbs from '@/components/rings/ProductBreadcrumbs.vue'
import Money from '@/components/Money.vue'
import TooltipCustom from '@/components/layout/tooltip/TooltipCustom.vue'
import ThSelect from '@/components/ui/inputs/ThSelect.vue'
import ProductGalleryNew from '@/components/layout/gallery/ProductGallery.vue'
import LazyTooltip from '@/components/layout/tooltip/LazyTooltip.vue'
import PageContentLoader from '@/components/ui/PageContentLoader.vue'
import ReviewsAggregate from '@/components/rings/ReviewsAggregate.vue'
import SaveForLaterButton from '@/components/saved-products/SaveForLaterButton.vue'
import PdpAccordion from '@/components/product/PdpAccordion.vue'
import DeliveryTimeframeExpress from '@/components/rings/DeliveryTimeframeExpress.vue'
import AuthFormInModal from '@/components/user/auth/AuthFormInModal.vue'
import { useInit } from '@/plugins/init/use-init.ts'
import AddToCartFloatingButton from '../components/AddToCartFloatingButton.vue'

const PageErrorFiveHundred = defineAsyncComponent({
    loader: () => import('@/components/PageErrorFiveHundred.vue'),
    hydrate: hydrateOnVisible(),
})

const PageErrorFourHundredFour = defineAsyncComponent({
    loader: () => import('@/components/PageErrorFourHundredFour.vue'),
    hydrate: hydrateOnVisible(),
})

const CmsBlocks = defineAsyncComponent({
    loader: () => import('@/components/cms/CmsBlocks.vue'),
    hydrate: hydrateOnIdle(),
})

const Reviews = defineAsyncComponent({
    loader: () => import('@/components/rings/Reviews.vue'),
    hydrate: hydrateOnVisible(),
})

const props = defineProps<{
    slug: string
    metal: MetalCode
    productType: JewelleryProductType
    suffixSlug: string | undefined
}>()

const formatter = useMoneyFormatter()
const localizeHrefs = useHrefLocalizer()
const routes = useRoutes()
const router = useRouter()
const redirect = useRedirect()
const { t } = useI18n()
const currency = useCurrencyInput()
const { enquiryFormContent } = useInit()

const { regionCode } = useRegionPlugin()
const { locale } = useTranslator()

const engraving = ref('')
const engravingMaxLength = 25

const path = useRoute().path

const hasEngraving = computed(() => Boolean(engraving.value.length))

const response = useJewelleryDetailsQuery({
    variables: computed(() => ({
        slug: props.slug,
        metal: props.metal,
        suffixSlug: props.suffixSlug || null,
        locale: locale.value,
        currency: currency.value,
        region: regionCode.value,
        page: path,
        pageFallback: '/jewellery',
        type: props.productType,
    })),
})

await response

const { fetching, data, error, executeQuery, stale } = response

const jewelleryItem = toRef(() => data.value?.catalogueJewellery)

const redirectIfNeeded = async () => {
    if (
        jewelleryItem.value &&
        (jewelleryItem.value.slug !== props.slug ||
            jewelleryItem.value.primaryMetal.systemCode !== props.metal ||
            jewelleryItem.value.suffixSlug !== props.suffixSlug)
    ) {
        await redirect(
            router.resolve(routes.ring.jewellery(jewelleryItem.value)).href,
            301
        )
    }
}
watch(jewelleryItem, redirectIfNeeded)

await redirectIfNeeded()

useProductAnalytics(jewelleryItem, fetching)

useProductPageMeta(
    toRef(() => data.value?.pageProductDetails?.meta),
    jewelleryItem,
    fetching,
    stale
)

const deliveryDate = useDeliveryDate(response, (data) =>
    !hasEngraving.value
        ? data.catalogueJewellery?.firstAvailableDeliveryDate
        : data.catalogueJewellery?.firstAvailableDeliveryDateHasEngraving
)

const responseErrors = useResponseError(response)

const isEnquiryModalOpen = ref(false)
const closeEnquiryModal = () => {
    isEnquiryModalOpen.value = false
}

const { isLogged } = useAuth()

const {
    handleRingSizeNotSelected,
    ringSizeError,
    ringSizeId,
    ringSizeOptions,
} = useRingSize(
    toRef(() => {
        return jewelleryItem.value?.__typename === 'CatalogueJewelleryRing'
            ? jewelleryItem.value.sizes
            : undefined
    })
)

const getInput = (): DesignStudioJewelleryInput | null => {
    if (!jewelleryItem.value) {
        return null
    }

    let ringSize: InputMaybe<RingSizeInput> = null

    if (jewelleryItem.value.__typename === 'CatalogueJewelleryRing') {
        ringSize = handleRingSizeNotSelected()

        if (!ringSize) {
            return null
        }
    }

    const uri = getUri()

    return {
        catalogueJewelleryCode: jewelleryItem.value.catalogueCode,
        engraving: engraving.value,
        uri,
        commissionJunction,
        ringSize,
    }
}

const existingCustomerResponse = useAddToCartJewelleryExistingCustomerMutation()
const {
    executeMutation: mutationExistingCustomer,
    fetching: fetchingExistingCustomer,
} = existingCustomerResponse
const handleAddToCart = () => {
    if (isLogged.value) {
        const input = getInput()

        if (!input) {
            return
        }

        const handleAddToCartSuccess = prepare()

        mutationExistingCustomer({
            input,
        }).then(({ error }) => {
            handleAddToCartSuccess({
                error,
                data: {
                    customer: undefined,
                },
            })
        })
    } else {
        isEnquiryModalOpen.value = true
    }
}

const commissionJunction = getCommissionJunctionInput()

const newCustomerResponse = useAddToCartJewelleryNewCustomerMutation()
const { executeMutation: mutationNewCustomer, fetching: fetchingNewCustomer } =
    newCustomerResponse

const { prepare } = useHandleAddToCartSuccess(jewelleryItem)

const { customerRegistrationInput } = storeToRefs(
    useCustomerRegistrationInputStore()
)

const isAdmin = useIsAdmin()

const submitNewCustomer = (successCallback: () => void) => {
    const input = getInput()
    const customer = customerRegistrationInput.value

    if (!input || !customer) {
        return
    }

    const handleAddToCartSuccess = prepare()
    mutationNewCustomer({
        input,
        customer,
        isConsultant: isAdmin.value,
    }).then(({ error, data: response }) => {
        const data =
            response?.createDesignStudioProductAndAddToCartJewelleryNewCustomer

        handleAddToCartSuccess({
            error,
            data,
        })

        if (data?.customer.login) {
            successCallback()
        }
    })
}

const errors = useLastResponseErrorAtPath([
    createResponseAndPath(
        existingCustomerResponse,
        'createDesignStudioProductAndAddToCartJewelleryExistingCustomer'
    ),
    createResponseAndPath(
        newCustomerResponse,
        'createDesignStudioProductAndAddToCartJewelleryNewCustomer'
    ),
])

const breadcrumbs = useJewelleryDetailsBreadcrumbs(
    jewelleryItem,
    toRef(() => props.productType)
)

const classProductWrapper = computed(() => ({
    fetching: fetching.value,
}))
const content = computed(
    () => enquiryFormContent.value['jewellery-add-to-cart']
)
</script>

<style lang="scss" scoped>
.product-additional-details {
    .product-description {
        font-size: 1rem;
        line-height: 1.6em;
        padding: 0 0 20px 0;
    }
}

.price-explainer-list {
    padding: 10px 15px;

    @include min-width(640) {
        padding: 0;
    }

    .item {
        margin-bottom: 15px;

        @include min-width(640) {
            display: flex;
            justify-content: space-between;
        }

        &:last-child {
            margin-bottom: 0;
        }

        .label {
            display: block;
            margin-bottom: 5px;
        }

        .value {
            display: block;
        }
    }

    .divider {
        @include border-bottom;

        margin-bottom: 15px;
    }
}

.product-wrapper {
    @include product-wrapper();

    .product-details {
        .product-information {
            margin: 0 0 35px;
            padding: 0;
            border-bottom: 0;

            &.last {
                margin: 50px 0 120px;
            }
        }

        .product-customization {
            margin: 0 0 25px;
            @include border-top;

            .metal {
                margin: 0 0 25px;
                padding: 25px 0;
                @include border-bottom;
            }

            .size-and-engraving {
                width: 100%;
                border-bottom: 1px solid rgb(234 233 232);
                padding: 0 0 25px;
                margin: 0 0 25px;

                @include min-width(640) {
                    display: flex;
                }

                .size {
                    @include min-width(640) {
                        width: 200px;
                    }
                }

                .engraving {
                    margin-top: 24px;

                    @include min-width(640) {
                        &:nth-child(2) {
                            margin: 0 0 0 20px;
                        }
                    }
                }
            }
        }
    }
}
</style>
